import { buildUndoData } from './streamHelpers';
function isMarkdownBlock(block) {
    return block.type === 'markdown';
}
/**
 * Plain text for clipboard: prefer persisted agent_response, then markdown blocks, then message.content.
 */
export function getCopyTextForAgentMessage(message) {
    var _a, _b;
    if (message.role !== 'agent' || !message.envelope) {
        return extractTextFromContent(message.content);
    }
    const fromEnvelope = (_a = message.envelope.agent_response) === null || _a === void 0 ? void 0 : _a.trim();
    if (fromEnvelope)
        return fromEnvelope;
    const fromBlocks = (_b = message.envelope.blocks) === null || _b === void 0 ? void 0 : _b.filter(isMarkdownBlock).map((b) => b.content.trim()).filter(Boolean).join('\n\n').trim();
    if (fromBlocks)
        return fromBlocks;
    return extractTextFromContent(message.content);
}
// ---------------------------------------------------------------------------
// Type Guards
// ---------------------------------------------------------------------------
export function isStoredAssistantContent(content) {
    return (typeof content === 'object' &&
        content !== null &&
        'text' in content &&
        typeof content.text === 'string');
}
export function extractTextFromContent(content) {
    if (typeof content === 'string')
        return content;
    if (isStoredAssistantContent(content))
        return content.text;
    return '';
}
/**
 * Replays the live SSE metadata-block semantics for persisted history.
 *
 * `action_bar` and `undo` are transport-only blocks: the live stream reducer
 * merges them into the preceding renderable block. Persisted messages retain
 * the original standalone blocks, so history must perform the same merge or
 * both controls disappear after a refresh because neither metadata block has
 * a standalone renderer.
 */
export function normalizeHistoryMetadataBlocks(blocks) {
    return blocks.reduce((normalized, block) => {
        if (block.type !== 'action_bar' && block.type !== 'undo') {
            normalized.push(block);
            return normalized;
        }
        if (normalized.length === 0)
            return normalized;
        const lastIndex = normalized.length - 1;
        const precedingBlock = normalized[lastIndex];
        if (block.type === 'action_bar') {
            normalized[lastIndex] = Object.assign(Object.assign({}, precedingBlock), { footerActions: block.actions });
            return normalized;
        }
        // IP-15624: shared with the live SSE reducer. This is the path that produced
        // the bug — a refresh replays the persisted block, so a field this merge
        // drops (`undone`) resets the button to a live "Undo" over an already-
        // reverted operation.
        normalized[lastIndex] = Object.assign(Object.assign({}, precedingBlock), { undoData: buildUndoData(block) });
        return normalized;
    }, []);
}
/** Normalize a persisted agent message before it enters the render pipeline. */
export function normalizeHistoryMessage(message) {
    var _a;
    if (message.role !== 'agent' || !((_a = message.envelope) === null || _a === void 0 ? void 0 : _a.blocks))
        return message;
    return Object.assign(Object.assign({}, message), { envelope: Object.assign(Object.assign({}, message.envelope), { blocks: normalizeHistoryMetadataBlocks(message.envelope.blocks) }) });
}
// ---------------------------------------------------------------------------
// Message Transformation
// ---------------------------------------------------------------------------
/**
 * Ensures agent messages have an agent_badge block at the beginning.
 * History messages from the API may not include this block, so we prepend it
 * if the envelope has an agent but no agent_badge block exists.
 */
export function ensureAgentBadge(message) {
    var _a;
    // Only process agent messages with an envelope and agent info
    if (message.role !== 'agent' || !((_a = message.envelope) === null || _a === void 0 ? void 0 : _a.agent)) {
        return message;
    }
    const { blocks = [] } = message.envelope;
    // Check if agent_badge block already exists
    const hasAgentBadge = blocks.some((block) => block.type === 'agent_badge');
    if (hasAgentBadge) {
        return message;
    }
    // Create agent_badge block and prepend it
    const agentBadgeBlock = {
        type: 'agent_badge',
        block_id: `agent-badge-${message.id}`,
        block_index: 0,
        agent: message.envelope.agent
    };
    // Update block indices for existing blocks and prepend agent_badge
    const updatedBlocks = [agentBadgeBlock, ...blocks.map((block, index) => (Object.assign(Object.assign({}, block), { block_index: index + 1 })))];
    return Object.assign(Object.assign({}, message), { envelope: Object.assign(Object.assign({}, message.envelope), { blocks: updatedBlocks }) });
}
